import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { CheckCircle, Wifi, Clock, Download } from "lucide-react";
import { useTranslation } from "react-i18next";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      border: `1px solid ${isDark ? "#444" : "#28a745"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
      overflow: "visible",
    },
  };
});

export default function SuccessModal({ open, onClose, amount, title, message, transactionId }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let timer;
    if (open) {
      setTimeout(() => setVisible(true), 100);
      timer = setTimeout(() => {
        handleClose();
      }, 15000);
    } else {
      setVisible(false);
    }
    return () => clearTimeout(timer);
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const downloadReceipt = () => {
    const receiptContent = `
      Bundle Activation Receipt
      Status: SUCCESSFUL
      Transaction ID: ${transactionId}
      Date: ${new Date().toLocaleDateString()}
      Time: ${new Date().toLocaleTimeString()}
      Amount: ${amount} AFN
      
      ${message}
      
      Thank you for using AFRH Merchant Portal
    `;
    
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Bundle_Receipt_${transactionId}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <div className="w-[400px] text-center relative overflow-visible">
        <div className="w-full px-6 pt-6 flex items-center justify-center relative z-10">
          <div className="relative">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={48} className="text-green-600 dark:text-green-400" />
            </div>
            
            <div className="absolute inset-0">
              <div className="absolute inset-0 border-4 border-green-200 dark:border-green-700 rounded-full animate-ping"></div>
              <div className="absolute inset-0 border-4 border-green-300 dark:border-green-600 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center mt-6">
          <h1 className="text-[24px] mb-2 font-bold text-gray-800 dark:text-white">
            {title || t('successModal.defaultTitle') || "Bundle Activation Initiated"}
          </h1>
        </div>

        <div className="px-4">
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {message || t('successModal.defaultMessage') || "Your bundle activation has been initiated successfully. Our team will process it shortly."}
          </p>
        </div>

        <div className={`flex items-center justify-center flex-col gap-1 py-3 mt-2 w-full rounded-md bg-gradient-to-r from-[#f0f9ff] to-[#f0fdf4] dark:from-gray-800 dark:to-gray-800 border border-[#d1fae5] dark:border-gray-700`}>
          <div className="flex items-center gap-2 mb-2">
            <Wifi size={20} className="text-green-600 dark:text-green-400" />
            <h3 className="text-[16px] font-semibold text-gray-800 dark:text-white">
              {t('successModal.bundleAmount') || "Bundle Amount"}
            </h3>
          </div>
          <p className="text-xl font-bold text-[#CD0C02] dark:text-red-500">
            {amount} AFN
          </p>
          
          {transactionId && (
            <div className="mt-2 text-sm">
              <p className="text-gray-600 dark:text-gray-400">
                {t('successModal.transactionId') || "Transaction ID"}:
              </p>
              <p className="font-mono text-gray-800 dark:text-gray-200">{transactionId}</p>
            </div>
          )}
        </div>

        <div className={`mt-4 p-3 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Clock size={16} className="text-blue-600 dark:text-blue-400" />
            <span className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-800'}`}>
              {t('successModal.processingInfo') || "Processing Information"}
            </span>
          </div>
          <ul className={`text-sm text-left space-y-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
            <li>• {t('successModal.note1') || "Will be processed by our backoffice team"}</li>
            <li>• {t('successModal.note2') || "You'll be notified upon completion"}</li>
            <li>• {t('successModal.note3') || "Check status in your activation history"}</li>
          </ul>
        </div>

        <div className="w-full flex items-center gap-3 mt-6">
          <button
            onClick={downloadReceipt}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-md transition-colors ${
              isDark 
                ? 'bg-blue-700 hover:bg-blue-800' 
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
          >
            <Download size={18} />
            {t('successModal.receipt') || "Receipt"}
          </button>
          <button
            onClick={handleClose}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-md transition-colors ${
              isDark 
                ? 'bg-red-700 hover:bg-red-800' 
                : 'bg-[#CD0C02] hover:bg-[#b30b02]'
            } text-white`}
          >
            {t('successModal.goBack') || "Go Back"}
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}